import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
// eslint-disable-next-line
import mapboxgl from "!mapbox-gl";
import geoViewport from "@mapbox/geo-viewport";
import { Leaf } from "phosphor-react";
import {
  StackAnalysisContext,
  StackAnalysisActions,
} from "context/stack_analysis";
import { useFieldsQuery } from "hooks/useFields";
import InputField from "components/Input";
import Button from "components/Button";
import "./index.scss";

const StackFieldsSelection = () => {
  const mapContainer = useRef(null);
  const { state, dispatch } = useContext(StackAnalysisContext);
  const { farmId } = useParams();
  const { isLoading, data } = useFieldsQuery(farmId);
  const [searchName, setSearchName] = useState("");
  const [map, setMap] = useState(null);
  const [mapFullyLoaded, setMapFullyLoaded] = useState(false);
  const { selectedFields } = state;

  useEffect(() => {
    if (isLoading) return;

    mapboxgl.accessToken = process.env.REACT_APP_MAPGL_ACCESS_TOKEN;
    const mapElement = mapContainer.current.getBoundingClientRect();
    const mapDimension = [mapElement.height, mapElement.width];
    const bounds = geoViewport.viewport(data.bbox, mapDimension, 0, 12.5, 512);
    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/satellite-v9",
      zoom: bounds.zoom,
      center: bounds.center,
    });
    setMap(newMap);
  }, [data, isLoading]);

  const toggleField = useCallback(
    (field) => {
      const newSelectedFields = selectedFields.includes(field)
        ? selectedFields.filter((id) => id !== field)
        : [...selectedFields, field];

      dispatch({
        type: StackAnalysisActions.SET_SELECTED_FIELDS,
        value: newSelectedFields,
      });
    },
    [dispatch, selectedFields]
  );

  useEffect(() => {
    if (!map) return;

    map.on("load", () => {
      map.addLayer({
        id: "fields-fill",
        type: "fill",
        source: {
          type: "geojson",
          data,
        },
        paint: {
          "fill-color": [
            "case",
            ["boolean", ["feature-state", "active"], false],
            "#09BA51",
            "#FFFFFF",
          ],
          "fill-opacity": [
            "case",
            ["boolean", ["feature-state", "tiled"], false],
            0,
            ["boolean", ["feature-state", "active"], false],
            0.2,
            ["boolean", ["feature-state", "hover"], false],
            0.5,
            0,
          ],
        },
        filter: ["==", "$type", "Polygon"],
      });

      map.addLayer({
        id: "fields-outline",
        type: "line",
        source: {
          type: "geojson",
          data,
        },
        paint: {
          "line-color": [
            "case",
            ["boolean", ["feature-state", "active"], false],
            "#09BA51",
            "#FFFFFF",
          ],
          "line-width": [
            "case",
            ["boolean", ["feature-state", "active"], false],
            2,
            3,
          ],
          "line-opacity": 0.7,
        },
        filter: ["==", "$type", "Polygon"],
      });

      setMapFullyLoaded(true);
    });
  }, [data, map]);

  useEffect(() => {
    if (!mapFullyLoaded) return;

    const onClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["fields-fill"],
      });

      if (!features.length) {
        return;
      }

      const feature = features[0];

      toggleField(feature.id, selectedFields);
    };

    map.on("click", "fields-fill", onClick);

    // eslint-disable-next-line consistent-return
    return () => {
      map.off("click", "fields-fill", onClick);
    };
  }, [map, mapFullyLoaded, selectedFields, toggleField]);

  useEffect(() => {
    if (!mapFullyLoaded) return;

    const fieldsFillLayer = map.getLayer("fields-fill");
    const fieldsOutlineLayer = map.getLayer("fields-outline");
    if (!fieldsFillLayer || !fieldsOutlineLayer) {
      return;
    }

    const allFields = map.querySourceFeatures("fields-fill", {
      sourceLayer: "fields-fill",
    });
    allFields.forEach((field) => {
      if (!selectedFields.includes(field.id)) {
        map.setFeatureState(
          { source: "fields-fill", id: field.id },
          { active: false }
        );
        map.setFeatureState(
          { source: "fields-outline", id: field.id },
          { active: false }
        );
      }
    });

    selectedFields.forEach((field) => {
      map.setFeatureState(
        { source: "fields-fill", id: field },
        { active: true }
      );
      map.setFeatureState(
        { source: "fields-outline", id: field },
        { active: true }
      );
    });
  }, [map, mapFullyLoaded, selectedFields]);

  useEffect(() => {
    if (!mapFullyLoaded) return;

    const fieldsFillLayer = map.getLayer("fields-fill");
    const fieldsOutlineLayer = map.getLayer("fields-outline");
    if (!fieldsFillLayer || !fieldsOutlineLayer) {
      return;
    }

    map.setFilter("fields-outline", [
      "in",
      ["downcase", searchName],
      ["downcase", ["string", ["get", "field_name"]]],
    ]);
    map.setFilter("fields-fill", [
      "in",
      ["downcase", searchName],
      ["downcase", ["string", ["get", "field_name"]]],
    ]);
  });

  const toggleAllFields = useCallback(() => {
    const newSelectedFields =
      selectedFields.length === data.features.length
        ? []
        : data.features.map((field) => parseInt(field.id, 10));

    dispatch({
      type: StackAnalysisActions.SET_SELECTED_FIELDS,
      value: newSelectedFields,
    });
  }, [data, dispatch, selectedFields.length]);

  const renderFilters = () => {
    return (
      <>
        <div className="field-name">
          <InputField
            placeholder="Search for field name..."
            type="text"
            id="field-name"
            className="search-field"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
        </div>
        <div
          className="select-all"
          onClick={() => toggleAllFields()}
          aria-hidden
        >
          {selectedFields.length === data.features.length
            ? `Deselect all fields (${data.features.length})`
            : `Select all fields (${data.features.length})`}
        </div>
      </>
    );
  };

  const renderFields = () => {
    return (
      <div className="fields">
        {data.features
          ?.filter((item) =>
            item.properties.field_name
              .toLowerCase()
              .includes(searchName.toLocaleLowerCase())
          )
          .map((field) => (
            <div
              className={`field ${
                selectedFields.includes(parseInt(field.id, 10))
                  ? "-selected"
                  : ""
              }`}
              key={field.id}
              onClick={() => toggleField(parseInt(field.id, 10))}
              aria-hidden
            >
              <div className="details">
                <div className="name">{field.properties.field_name}</div>
                <div className="properties">
                  <div className="crop-type">
                    {field.properties.field_crop_type || "Others"}
                  </div>
                  <div className="separator" />
                  <div className="hectares">{field.properties.area} Ha</div>
                </div>
              </div>
            </div>
          ))}
      </div>
    );
  };

  const renderPageAction = () => {
    return (
      <div className="page-action">
        <Button
          type="solid"
          label="Next"
          color="green"
          rounded
          block
          disabled={!selectedFields.length}
          onClick={() =>
            dispatch({
              type: StackAnalysisActions.SET_PAGE,
              value: "seasons",
            })
          }
        />
      </div>
    );
  };

  const totalAreaSelected = () => {
    if (selectedFields.length === 0) return 0;

    const fieldsSelected = data.features.filter((field) =>
      selectedFields.includes(field.properties.id)
    );
    const totalArea = fieldsSelected.reduce(
      (accumulator, feature) => accumulator + feature.properties.area,
      0
    );

    return totalArea.toFixed(1);
  };

  if (isLoading) return null;

  return (
    <div className="stack-fields-selection">
      <div className="fields-wrapper">
        <div className="list-options">
          <Leaf size={32} color="#339DFF" className="icon" />
          <h2 className="title">Choose Fields</h2>
          {renderFilters()}
        </div>
        {renderFields()}
        {renderPageAction()}
      </div>
      <div className="stack-map-wrapper">
        <div className="total-hectares">{`Total Area Selected: ${totalAreaSelected()} Ha`}</div>
        <div className="map-container" ref={mapContainer} />
      </div>
    </div>
  );
};

export default StackFieldsSelection;
